import { StatusCode } from "api/protocols";
import {
  GetReservationModuleAll,
  PostReservationLocation,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import axios, { CancelToken } from "axios";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import SelectNew from "components/select/SelectNew";
import { useAppDispatch } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { showError, showSuccess } from "redux/actions/ui";
import { IModule } from "redux/reducers/models/reservations";
import { useTranslation } from "react-i18next";

interface IState {
  title: string;
  moduleId: number;
  modules: Array<IModule>;
}

export default function LocationNew() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [state, setState] = useState<IState>({
    title: "",
    moduleId: null,
    modules: undefined,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void getModules(source.token);

    return () => source.cancel();
  }, []);

  async function getModules(cancelToken: CancelToken) {
    const res = await GetReservationModuleAll(null, false);
    let moduleId: number = null;
    if (res.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting modules")));
    } else {
      //Pre select the first module in the array
      moduleId = res?.data?.length > 0 ? res?.data[0]?.id : null;
    }

    setState(prevState => ({
      ...prevState,
      modules: res?.status !== StatusCode.OK ? [] : res?.data,
      moduleId: moduleId,
    }));
  }

  async function createNewLocation() {
    const res = await PostReservationLocation({ reservation_module_id: state.moduleId, title: state.title }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving location")));
      return;
    }
    dispatch(showSuccess(t("Successfully created location")));
    history.push("/admin/settings/reservations/modules");
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  return (
    <Page
      title={t("New Location")}
      subtitle={t("Create a new location for a module")}
      narrow
      primaryAction={{
        content: t("Save"),
        action: () => createNewLocation(),
        disabled: !state.title || !state.moduleId,
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Modules"),
          url: "/admin/settings/reservations/modules",
        },
      ]} //TODO: Translation
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              {/* //TODO: Translation */}
              <Input
                label={t("Title")}
                placeholder={t("Title")}
                value={state.title}
                onChange={handleInputChange}
                id="title"
              />
              <SelectNew
                id={"id"}
                value={state.moduleId}
                onChange={value => setState(prevState => ({ ...prevState, moduleId: value }))}
                label={t("Module")}
                isLoading={state.modules === undefined}
                options={state.modules?.map(module => ({
                  id: module.id,
                  label: module.title,
                }))}
                searchable
                placeholder={t("Search modules...")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
